import { useEffect, useState } from "react";
import { PD } from "@/constants/testIds";
import {
  SEARCH_MAX_RESULTS_DEFAULT,
  SEARCH_MAX_RESULTS_LIMIT,
  addSearchField,
  availableSearchFields,
  defaultSearchFields,
  defaultSearchTitle,
  emptySearchContent,
  loadSearchDataSources,
  moveSearchField,
  removeSearchField,
  sanitizeSearchContent,
  searchSource,
  selectedSearchFields,
} from "@/lib/pageDesignerSearch";

function StepDot({ n, current, label }) {
  return (
    <span className={`epd__step${current === n ? " is-active" : ""}`}>
      {n}. {label}
    </span>
  );
}

/**
 * Search Builder — Choose Data Source → Choose Search Fields → Settings.
 *
 * The data source list and the field list both come from the server. This
 * component never carries its own table or column names, so every table the
 * server advertises is selectable here without a change to the builder.
 */
export default function PageDesignerSearchBuilder({ card, open, onClose, onSave }) {
  const [step, setStep] = useState(1);
  const [draft, setDraft] = useState(emptySearchContent());
  const [sources, setSources] = useState([]);
  const [sourcesStatus, setSourcesStatus] = useState("loading");
  const [maxResultsLimit, setMaxResultsLimit] = useState(SEARCH_MAX_RESULTS_LIMIT);

  useEffect(() => {
    if (!open) return undefined;
    setStep(1);
    const current = sanitizeSearchContent(card?.searchContent);
    setDraft(current.sourceId ? current : emptySearchContent());
    setSourcesStatus("loading");
    let cancelled = false;
    loadSearchDataSources().then((result) => {
      if (cancelled) return;
      setSources(result.sources);
      setMaxResultsLimit(result.maxResultsLimit);
      setSourcesStatus(result.status);
    });
    return () => {
      cancelled = true;
    };
  }, [open, card?.id]);

  if (!open) return null;

  const source = searchSource(sources, draft.sourceId);
  const sourceFields = source?.fields || [];
  const available = availableSearchFields(draft, sourceFields);
  const selected = selectedSearchFields(draft, sourceFields);
  const canNextFromSource = Boolean(draft.sourceId);
  const canNextFromFields = draft.fields.length > 0;
  const canSave = canNextFromSource && canNextFromFields;

  const patch = (partial) => setDraft((current) => sanitizeSearchContent({ ...current, ...partial }));

  const chooseSource = (sourceId) => {
    const nextSource = searchSource(sources, sourceId);
    const keepTitle =
      draft.title.trim() && draft.title !== defaultSearchTitle(searchSource(sources, draft.sourceId));
    setDraft(
      sanitizeSearchContent({
        ...emptySearchContent(),
        sourceId: nextSource ? nextSource.id : "",
        title: keepTitle ? draft.title : defaultSearchTitle(nextSource),
        fields: defaultSearchFields(nextSource),
        maxResults: draft.maxResults || SEARCH_MAX_RESULTS_DEFAULT,
      }),
    );
  };

  const save = () => {
    if (!canSave) return;
    onSave(sanitizeSearchContent(draft));
  };

  return (
    <div className="epd__modal" role="dialog" aria-modal="true" data-testid={PD.searchBuilder}>
      <div className="epd__modal-card epd__modal-card--wide">
        <h3>Search Builder</h3>
        <p>
          Configure search content for {card?.title || "this card"}. The card remains the container.
        </p>
        <div className="epd__steps">
          <StepDot n={1} current={step} label="Choose Data Source" />
          <StepDot n={2} current={step} label="Choose Search Fields" />
          <StepDot n={3} current={step} label="Settings" />
        </div>

        {step === 1 ? (
          <label className="epd__field" htmlFor="epd-search-source">
            <span className="epd__label">Data Source</span>
            <select
              id="epd-search-source"
              data-testid={PD.searchSource}
              className="epd__select browser-default"
              value={draft.sourceId}
              onChange={(event) => chooseSource(event.target.value)}
            >
              <option value="">Select data source</option>
              {sources.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label}
                </option>
              ))}
            </select>
            <span className="epd__tip" data-testid={PD.searchSourceHint}>
              {sourcesStatus === "loading"
                ? "Loading available EMRbyMD data sources…"
                : sourcesStatus === "error"
                  ? "Data sources are unavailable. Check your session and try again."
                  : source
                    ? `Searches the existing ${source.table} data through ${source.api}. No new database table is created and no query is sent from the browser.`
                    : "Every existing EMRbyMD table you are authorized to read is listed here."}
            </span>
          </label>
        ) : null}

        {step === 2 ? (
          <div className="epd__col-board">
            <div>
              <div className="epd__label">Available Search Fields</div>
              <div className="epd__col-list" data-testid={PD.searchAvailable}>
                {available.length === 0 ? (
                  <p className="epd__tip">All searchable fields are selected.</p>
                ) : (
                  available.map((field) => (
                    <button
                      key={field.id}
                      type="button"
                      className="epd__stack-item"
                      data-testid={`${PD.searchFieldAdd}-${field.id}`}
                      onClick={() => setDraft((current) => addSearchField(current, field.id))}
                    >
                      <span className="epd__stack-title">{field.label}</span>
                    </button>
                  ))
                )}
              </div>
            </div>
            <div>
              <div className="epd__label">Fields the search box searches</div>
              <div className="epd__col-list" data-testid={PD.searchSelected}>
                {selected.length === 0 ? (
                  <p className="epd__tip">Select at least one field.</p>
                ) : (
                  selected.map((field) => (
                    <div key={field.id} className="epd__col-selected">
                      <span className="epd__stack-title">{field.label}</span>
                      <span className="epd__col-actions">
                        <button
                          type="button"
                          className="epd__btn epd__btn--ghost epd__btn--tiny"
                          data-testid={`${PD.searchFieldUp}-${field.id}`}
                          onClick={() => setDraft((current) => moveSearchField(current, field.id, "up"))}
                        >
                          Up
                        </button>
                        <button
                          type="button"
                          className="epd__btn epd__btn--ghost epd__btn--tiny"
                          data-testid={`${PD.searchFieldDown}-${field.id}`}
                          onClick={() =>
                            setDraft((current) => moveSearchField(current, field.id, "down"))
                          }
                        >
                          Down
                        </button>
                        <button
                          type="button"
                          className="epd__btn epd__btn--ghost epd__btn--tiny"
                          data-testid={`${PD.searchFieldRemove}-${field.id}`}
                          onClick={() => setDraft((current) => removeSearchField(current, field.id))}
                        >
                          Remove
                        </button>
                      </span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div>
            <label className="epd__field" htmlFor="epd-search-title">
              <span className="epd__label">Title</span>
              <input
                id="epd-search-title"
                data-testid={PD.searchTitle}
                className="epd__input"
                value={draft.title}
                onChange={(event) => patch({ title: event.target.value })}
              />
            </label>
            <label className="epd__field" htmlFor="epd-search-rows">
              <span className="epd__label">Maximum results</span>
              <input
                id="epd-search-rows"
                data-testid={PD.searchMaxResults}
                className="epd__input"
                type="number"
                min={1}
                max={maxResultsLimit}
                value={draft.maxResults}
                onChange={(event) => patch({ maxResults: event.target.value })}
              />
            </label>
            <p className="epd__tip">
              The server enforces this limit and the authorization of the selected data source. A
              search with an empty box does not read the table.
            </p>
          </div>
        ) : null}

        <div className="epd__modal-actions">
          <button
            type="button"
            className="epd__btn epd__btn--ghost"
            data-testid={PD.searchBuilderCancel}
            onClick={onClose}
          >
            Cancel
          </button>
          {step > 1 ? (
            <button
              type="button"
              className="epd__btn epd__btn--ghost"
              data-testid={PD.searchBuilderBack}
              onClick={() => setStep((current) => current - 1)}
            >
              Back
            </button>
          ) : null}
          {step < 3 ? (
            <button
              type="button"
              className="epd__btn"
              data-testid={PD.searchBuilderNext}
              disabled={step === 1 ? !canNextFromSource : !canNextFromFields}
              onClick={() => setStep((current) => current + 1)}
            >
              Next
            </button>
          ) : (
            <button
              type="button"
              className="epd__btn"
              data-testid={PD.searchBuilderSave}
              disabled={!canSave}
              onClick={save}
            >
              Save Content
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
